import { render } from '@oskecare/fe-utilities/lib/LitElement/index.js';
import { constructPath } from '@oskecare/fe-utilities/scripts/helpers.js';
import {
  AUTH_INIT_FAILED,
  AUTH_INIT_REDIRECTING,
} from '@oskecare/infrastructure/src/modules/AuthManager/index.js';
import { routes, redirects } from './routes.js';
import { config } from './config.js';
import {
  authManager,
  customerDataManager,
  errorHandler,
  logger,
  ost,
  tagManager,
  router,
} from './infrastructure.js';
import renderLayout from './pages/layout.js';
import renderNotFoundPage from './pages/404.js';
import renderIePage from './pages/ie.js';
import renderErrorPage from './pages/error.js';

/**
 * Removes the initialization "beating heart" splash screen
 */
function removeSplashScreen() {
  const splash = document.getElementById('splash');
  splash.parentNode.removeChild(splash);
}

/**
 * Handling /auth path - this has to be done before Router initialization, so history is manipulated manually
 *
 * @return {Promise<string>} true if AuthManager was initialized or identity switch redirect has started
 */
async function handleAuth() {
  const queryString = window.location.search;
  const params = new URLSearchParams(queryString);

  const state = params.get('state') || '/';
  const logoutCode = params.get('logout_code') || '';
  const code = params.get('code') || '';

  if (logoutCode) {
    // Case 1: Coming to /auth as part of partial logout (a.k.a. identity switching) journey, continue via AuthManager
    if (logoutCode === '{SUCCESS}') {
      authManager.switchIdentity(state);
      return AUTH_INIT_REDIRECTING;
    }
    return AUTH_INIT_FAILED;
  }

  if (code) {
    // Case 2: Return from SSO with CODE, important distinction is that returning to SSO is not
    // possible, so history state has to be replaced, not pushed
    const authInitStatus = await authManager.initialize(code);
    const returnPath = constructPath(state, config.basePath);
    window.history.replaceState(window.history.state, document.title, returnPath);
    return authInitStatus;
  }

  // Case 3: Redirected to /auth by Ecare itself due to expired or missing auth token (includes first visit)
  const authInitStatus = await authManager.initialize();
  const returnPath = constructPath(state, config.basePath);
  window.history.pushState(window.history.state, document.title, returnPath);
  return authInitStatus;
}

/**
 * Load the script for the Genesys Chat client.
 *
 * Genesys script has hard-coded imports of relative paths that only work on orange.sk and orange.test domains.
 * As such the script won't get loaded on local or dev environments.
 */
function setupGenesysChat() {
  if (!/^(www\.)?orange\.(sk|test)$/.test(window.location.host)) {
    return;
  }
  const script = document.createElement('script');
  script.src = config.genesysChatScriptUrl;
  document.body.appendChild(script);
}

/**
 * Navigate to error page, used to handle fatal errors during initialization.
 * Error page bypasses most of the application initialization.
 *
 * @see initializeApplication
 */
function goToError() {
  const errorPath = constructPath('chyba', config.basePath);
  window.location.assign(errorPath);
}

/**
 * Main application entrypoint, mainly responsible for initializing infrastructure modules.
 * Page content will be handled by Router handlers.
 *
 * @return {Promise<void>}
 */
async function initializeApplication() {
  // Set global settings for widgets
  window.config = config;

  const rootElement = window.document.getElementById('root');

  // Detect IE & Legacy Edge
  if (window.ActiveXObject || 'ActiveXObject' in window || /Edge/.test(navigator.userAgent)) {
    render(renderIePage(), rootElement);
    removeSplashScreen();
    return;
  }

  setupGenesysChat();

  if (/\/chyba$/.test(window.location.pathname)) {
    render(renderErrorPage(), rootElement);
    removeSplashScreen();
    return;
  }

  try {
    // NOTE: Initialization order is critical!
    logger.initialize();

    let authStatus;
    if (/\/auth$/.test(window.location.pathname)) {
      authStatus = await handleAuth();
    } else {
      authStatus = await authManager.initialize();
    }

    if (authStatus === AUTH_INIT_FAILED) {
      goToError();
      return;
    }
    if (authStatus === AUTH_INIT_REDIRECTING) {
      return;
    }

    logger.initializeDasetFlag();

    errorHandler.initialize(); // Error handling and logging available for all inits bellow.
    await customerDataManager.initialize();
    tagManager.initialize();
    router.initialize({
      rootElement,
      routes,
      redirects,
      basePath: config.basePath,
      layoutTemplate: renderLayout,
      notFoundPageTemplate: renderNotFoundPage,
    });

    ost.initialize();
    removeSplashScreen();
    customerDataManager.checkIsB2BClient();

    logger.info(`App initialized, URL: ${window.location.pathname}`, 'visit');
  } catch (error) {
    try {
      await logger.logErrorObject(error);
    } finally {
      goToError();
    }
    throw error;
  }
}

if (document.readyState === 'complete') {
  initializeApplication();
} else {
  window.onload = initializeApplication;
}
